/** V9-A — Carte jalon : date principale visible, détail (prévisionnel / planifié / réalisé), actions et historique en dépliant. Mobile-first. */
import { useState } from "react";
import { CaretDown, CaretUp, ClockCounterClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUT = {
  vide: { label: "À dater", cls: "bg-slate-100 text-slate-500" },
  previsionnel: { label: "Prévisionnel", cls: "bg-slate-100 text-slate-600" },
  planifie: { label: "Planifié", cls: "bg-blue-100 text-blue-800" },
  reporte: { label: "Reporté", cls: "bg-amber-100 text-amber-800" },
  realise: { label: "Réalisé", cls: "bg-emerald-100 text-emerald-800" },
  annule: { label: "Annulé", cls: "bg-rose-100 text-rose-800" },
};
const ACTION_LABEL = { previsionnel: "Prévision", previsionnel_auto: "Prévision auto", planifier: "Planifié", reporter: "Reporté", annuler: "Annulé", retablir: "Rétabli", realiser: "Réalisé", effacer: "Corrigé (effacé)" };
const fmt = (iso) => (iso ? new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString("fr-FR") : "—");
const today = () => new Date().toISOString().slice(0, 10);

function ActionBtn({ label, onClick, tone = "default", testid, disabled }) {
  const cls = tone === "primary" ? "bg-slate-900 text-white hover:bg-slate-700" : tone === "danger" ? "bg-rose-50 text-rose-700 hover:bg-rose-100" : "bg-slate-100 text-slate-700 hover:bg-slate-200";
  return <button type="button" disabled={disabled} onClick={onClick} data-testid={testid} className={`text-[11px] font-extrabold rounded-lg px-2.5 py-1.5 transition disabled:opacity-40 ${cls}`}>{label}</button>;
}

function DateLine({ label, value, kind, source }) {
  return (
    <div className="flex items-center justify-between text-[12px]" data-testid={`jalon-line-${kind}`}>
      <span className="text-slate-500">{label}{source ? <span className="text-slate-400"> · {source === "auto" ? "auto" : "manuel"}</span> : null}</span>
      <span className={value ? "font-bold text-slate-900" : "text-slate-300"}>{fmt(value)}</span>
    </div>
  );
}

export function JalonActions({ dossier, jalonKey, onUpdated, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [mode, setMode] = useState(null); // {action, needsDate, needsMotif}
  const [dateVal, setDateVal] = useState("");
  const [motif, setMotif] = useState("");
  const [busy, setBusy] = useState(false);
  const v = dossier?.jalons_view?.[jalonKey];
  if (!v) return null;
  const st = STATUT[v.statut] || STATUT.vide;
  const isRdv = jalonKey === "rdv_bancaire";

  const run = async (action, date, m) => {
    setBusy(true);
    try {
      const r = await api.post(`/dossiers/${dossier.id}/jalons/${jalonKey}`, { action, date: date || null, motif: m || null });
      onUpdated?.(r.data); setMode(null); setDateVal(""); setMotif("");
      toast.success(`${v.label} : ${ACTION_LABEL[action] || action}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
    finally { setBusy(false); }
  };
  const start = (action, needsDate = true, needsMotif = false, preset = "") => { setMode({ action, needsDate, needsMotif }); setDateVal(preset); setMotif(""); };

  return (
    <div className="mt-1.5 rounded-xl border border-slate-100 bg-white/70" data-testid={`jalon-card-${jalonKey}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-left" data-testid={`jalon-toggle-${jalonKey}`}>
        <div className="flex items-center gap-2 min-w-0">
          <span className={`text-[10px] font-extrabold rounded px-1.5 py-0.5 ${st.cls}`} data-testid={`jalon-statut-${jalonKey}`}>{st.label}{v.reports_count > 0 ? ` ×${v.reports_count}` : ""}</span>
          {v.main_date && <span className="text-[12px] font-bold text-slate-800" data-testid={`jalon-main-${jalonKey}`}>{fmt(v.main_date)}</span>}
          {v.overdue && <span className="text-[10px] font-bold text-amber-700">dépassé</span>}
        </div>
        {open ? <CaretUp size={12} /> : <CaretDown size={12} />}
      </button>
      {open && (
        <div className="px-2.5 pb-2.5 space-y-2" data-testid={`jalon-detail-${jalonKey}`}>
          <div className="space-y-0.5">
            {v.can_previsionnel && <DateLine label="Prévisionnel" value={v.previsionnel_date} kind="previsionnel" source={v.previsionnel_source} />}
            <DateLine label="Planifié" value={v.planifie_date} kind="planifie" />
            <DateLine label="Réalisé" value={v.realise_date} kind="realise" />
          </div>
          {!mode && (
            <div className="flex flex-wrap gap-1.5">
              {v.can_previsionnel && !v.realise_date && <ActionBtn label="Prévoir" onClick={() => start("previsionnel")} testid={`jalon-btn-previsionnel-${jalonKey}`} />}
              {v.can_previsionnel && v.previsionnel_source === "manual" && !isRdv && <ActionBtn label="Prévision auto" onClick={() => run("previsionnel_auto")} testid={`jalon-btn-previsionnel_auto-${jalonKey}`} />}
              {!v.realise_date && v.can_planifier && !v.planifie_date && <ActionBtn label="Planifier" onClick={() => start("planifier")} testid={`jalon-btn-planifier-${jalonKey}`} />}
              {!v.realise_date && v.planifie_date && <ActionBtn label="Reporter" onClick={() => start("reporter", true, true)} testid={`jalon-btn-reporter-${jalonKey}`} />}
              {!v.realise_date && v.planifie_date && <ActionBtn label="Annuler" tone="danger" onClick={() => (isRdv ? run("annuler") : start("annuler", false, true))} testid={`jalon-btn-annuler-${jalonKey}`} />}
              {!v.realise_date && v.statut === "annule" && !isRdv && <ActionBtn label="Rétablir" onClick={() => start("retablir")} testid={`jalon-btn-retablir-${jalonKey}`} />}
              {!v.realise_date && (!isRdv || v.planifie_date) && <ActionBtn label="Marquer réalisé" tone="primary" onClick={() => start("realiser", true, false, today())} testid={`jalon-btn-realiser-${jalonKey}`} />}
              {v.realise_date && !isRdv && <ActionBtn label="Corriger (effacer)" tone="danger" onClick={() => start("effacer", false, true)} testid={`jalon-btn-effacer-${jalonKey}`} />}
            </div>
          )}
          {mode && (
            <div className="rounded-lg bg-slate-50 p-2 space-y-2" data-testid={`jalon-form-${jalonKey}`}>
              <div className="text-[11px] font-extrabold text-slate-700">{ACTION_LABEL[mode.action]}{mode.action === "realiser" ? " — date passée ou du jour" : mode.action === "planifier" || mode.action === "reporter" || mode.action === "previsionnel" ? " — date future possible" : ""}</div>
              {mode.needsDate && <input type="date" value={dateVal} max={mode.action === "realiser" ? today() : undefined} onChange={(e) => setDateVal(e.target.value)} className="w-full rounded-lg border border-slate-200 p-2 text-[14px]" data-testid={`jalon-date-${jalonKey}`} />}
              {mode.needsMotif && <input type="text" value={motif} onChange={(e) => setMotif(e.target.value)} placeholder={mode.action === "effacer" ? "Motif (obligatoire)" : "Motif (facultatif)"} className="w-full rounded-lg border border-slate-200 p-2 text-[13px]" data-testid={`jalon-motif-${jalonKey}`} />}
              <div className="flex gap-1.5">
                <ActionBtn label="Valider" tone="primary" disabled={busy || (mode.needsDate && !dateVal) || (mode.action === "effacer" && !motif.trim())} onClick={() => run(mode.action, dateVal, motif)} testid={`jalon-submit-${jalonKey}`} />
                <ActionBtn label="Fermer" onClick={() => setMode(null)} testid={`jalon-cancel-form-${jalonKey}`} />
              </div>
            </div>
          )}
          {v.history?.length > 0 && (
            <details className="text-[11px]" data-testid={`jalon-history-${jalonKey}`}>
              <summary className="cursor-pointer text-slate-500 flex items-center gap-1"><ClockCounterClockwise size={12} /> Historique ({v.history.length})</summary>
              <ul className="mt-1 space-y-0.5 max-h-40 overflow-auto">
                {[...v.history].reverse().slice(0, 50).map((h, i) => (
                  <li key={i} className="text-slate-600">
                    <span className="font-bold">{ACTION_LABEL[h.action] || h.action}</span>
                    {h.to?.planifie_date || h.to?.date ? ` → ${fmt(h.to.planifie_date || h.to.date)}` : ""}
                    {h.to?.realise_date && h.action === "realiser" ? ` → ${fmt(h.to.realise_date)}` : ""}
                    <span className="text-slate-400"> · {new Date(h.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}{h.source && h.source !== "jalons" ? ` · ${h.source}` : ""}</span>
                    {h.motif ? <span className="italic"> — {h.motif}</span> : null}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

export function JalonsAvalBlock({ dossier, onUpdated }) {
  if (!dossier?.jalons_view) return null;
  const keys = ["offre_recue_client", "offre_signee", "deblocage_fonds"];
  return (
    <div className="card-base p-5 md:p-6 mb-4" data-testid="jalons-aval-block">
      <div className="overline mb-1">Jalons aval (offre · déblocage)</div>
      <p className="text-[11px] text-slate-400 mb-2">Prévisionnel = estimation · Planifié = programmé · Réalisé = constaté. Seule une offre <strong>reçue (réalisée)</strong> déclenche le délai de réflexion.</p>
      {dossier.jalons_warnings?.length > 0 && (
        <div className="mb-2 rounded-lg bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-900" data-testid="jalons-warnings">
          {dossier.jalons_warnings.map((w, i) => <div key={i}>⚠️ {w.message}</div>)}
        </div>
      )}
      {keys.map((k) => (
        <div key={k} className="py-1.5">
          <div className="text-[12.5px] font-bold text-slate-800">{dossier.jalons_view[k].label}</div>
          <JalonActions dossier={dossier} jalonKey={k} onUpdated={onUpdated} />
        </div>
      ))}
      {dossier.next_step && <div className="mt-2 text-[11.5px] text-slate-600" data-testid="jalons-next-step">Prochaine échéance connue : <strong>{dossier.next_step.label}</strong> le {fmt(dossier.next_step.date)} ({dossier.next_step.kind === "planifie" ? "planifié" : "prévisionnel"})</div>}
    </div>
  );
}
